/**
 * SceneElementLOD — Per-Element Opacity/Visibility Control by Quality Tier
 *
 * Adapted from AstroMonix LOD: controls opacity and rendering decisions
 * for each scene element type based on the current quality tier.
 *
 * GOLDEN RULE (from AstroMonix): NEVER set visible=false on critical elements.
 * Always degrade via opacity reduction. Hiding causes permanent disappearance
 * under sustained low FPS since recovery requires many consecutive good frames.
 *
 * Elements managed:
 * - Grid/background lines
 * - Node glows
 * - Connection/edge lines
 * - Particles
 * - Atmosphere effects
 * - Energy fields
 * - Lightning effects
 * - Limb systems
 * - Stats panel updates
 */

import { QualityTier, LodScalars, TIER_TO_VISUAL_LOD } from './lodQualityManager';

export interface SceneElementState {
  /** Grid opacity (applied as globalAlpha multiplier) */
  gridAlpha: number;
  /** Show every Nth grid line (0 = all, 1 = every other, etc.) */
  gridLineSkip: number;
  /** Edge/connection opacity multiplier */
  edgeAlpha: number;
  /** Maximum edge render distance in canvas pixels */
  maxEdgeDistance: number;
  /** Node glow intensity multiplier */
  glowMultiplier: number;
  /** Particle emission rate multiplier */
  particleMultiplier: number;
  /** Whether to render background atmosphere */
  showAtmosphere: boolean;
  /** Whether to render energy fields */
  showEnergyFields: boolean;
  /** Whether to render lightning/zap effects */
  showLightning: boolean;
  /** Whether to render creature limbs */
  showLimbs: boolean;
  /** Whether to update stats panel HTML this frame */
  updateStatsPanel: boolean;
  /** Corresponding visualEvolution LOD level (0-2 mapped to 5 tiers) */
  visualLODLevel: number;
  /** Current quality tier name */
  tier: QualityTier;
}

/**
 * Compute per-element rendering state from quality tier scalars.
 * Pure function — no mutable state.
 */
export function computeSceneElementState(
  tier: QualityTier,
  scalars: LodScalars,
  updateStats: boolean,
  panelBusy: boolean,
  catchup: boolean
): SceneElementState {
  return {
    gridAlpha: scalars.gridOpacity,
    gridLineSkip: scalars.gridLineSkip,
    edgeAlpha: scalars.edgeOpacity,
    maxEdgeDistance: scalars.maxConnectionDistance,
    glowMultiplier: scalars.glowIntensityMultiplier,
    particleMultiplier: scalars.particleMultiplier,
    showAtmosphere: scalars.renderAtmosphere,
    showEnergyFields: scalars.renderEnergyFields,
    showLightning: scalars.renderLightning,
    showLimbs: scalars.renderLimbs,
    // Stats panel: skip during busy unless catchup
    updateStatsPanel: updateStats && (!panelBusy || catchup),
    visualLODLevel: TIER_TO_VISUAL_LOD[tier],
    tier
  };
}

/**
 * Check if a specific edge should be rendered given current LOD state.
 * Applies distance culling at lower quality tiers.
 */
export function shouldRenderEdge(
  x1: number, y1: number,
  x2: number, y2: number,
  maxDistance: number
): boolean {
  if (!isFinite(maxDistance)) return true;
  const dx = x2 - x1;
  const dy = y2 - y1;
  return (dx * dx + dy * dy) <= (maxDistance * maxDistance);
}

/**
 * Check if a glow should be rendered based on LOD level and glow intensity.
 * At lower LOD, only render bright glows to save fill-rate.
 */
export function shouldRenderGlow(glowIntensity: number, visualLODLevel: number): boolean {
  if (visualLODLevel === 0) return true;
  if (visualLODLevel === 1) return glowIntensity > 0.3;
  // LOD 2+: only very bright glows
  return glowIntensity > 0.6;
}

/**
 * Determine if particle emission should happen this frame.
 * Uses random sampling proportional to the multiplier.
 */
export function shouldEmitParticle(particleMultiplier: number): boolean {
  if (particleMultiplier >= 1.0) return true;
  return Math.random() < particleMultiplier;
}

/**
 * Get the grid line visibility test function for the current LOD.
 * Returns a function that takes a line index and returns whether to show it.
 */
export function getGridLineFilter(skip: number): (index: number) => boolean {
  if (skip <= 0) return () => true;
  const modulus = skip + 1;
  return (index: number) => (index % modulus) === 0;
}
